import { useForm } from 'react-hook-form';
import { ThemeButton } from '@shared/ui/buttons';
import { DialogContent, DialogActions } from '@mui/material';
import { useState, useEffect, useMemo, useCallback } from 'react';
import TabsComponent from '@shared/ui/TabsComponent';
import { roles, getUserRole } from '@shared/static/roles';
import { validateServerErrors } from '@utils/validation';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useEditMemberDialog } from '@utils/dialogs-hooks';
import { useGroups } from '@app/api/groups/useGroups';
import { fetchAllGroups } from '@app/store/slices/myTeamSlice';
import {
  EmailInput,
  TelegramInput,
  InviteTeamPicker,
  MemberNameInput,
} from '@shared/ui/forms/inputs';
import { FormGroup } from '@shared/ui/forms/display';
import { useMembers } from '@app/api/members/useMembers';

export default function NewMemberDialog({ close, data }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);

  const [isLoading, setIsLoading] = useState(false);
  const [role, setRole] = useState(roles.TEAM_MEMBER);
  const [groupsWithoutTeamLead, setGroupsWithoutTeamLead] = useState([]);

  // Кеширование данных на время жизни модалки
  const [allGroupsLoaded, setAllGroupsLoaded] = useState(false);
  const [groupsWithoutTeamLeadLoaded, setGroupsWithoutTeamLeadLoaded] =
    useState(false);

  const { inviteMember } = useMembers();
  const { openEditMemberDialog } = useEditMemberDialog();
  const { fetchGroupsWithoutTeamLead } = useGroups();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = useForm({
    defaultValues: {
      telegram: '',
      group_id: data?.id || null,
    },
  });

  // Определяем доступные роли в зависимости от роли текущего пользователя
  // Если пользователь - тимлид, он может создавать только обычных участников
  const memberRoles = useMemo(() => {
    const member = {
      label: t(getUserRole(roles.TEAM_MEMBER)),
      value: roles.TEAM_MEMBER,
    };

    const lead = {
      label: t(getUserRole(roles.TEAM_LEAD)),
      value: roles.TEAM_LEAD,
    };

    return user?.group_role === roles.TEAM_LEAD ? [member] : [member, lead];
  }, [user?.group_role, t]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const res = await inviteMember({
        group_role: role,
        ...data,
      });

      if (res) {
        close();
        openEditMemberDialog({ isNew: true, ...res });
      }
    } catch (error) {
      validateServerErrors(setError, error);
    } finally {
      setIsLoading(false);
    }
  };

  // Установка group_id при инициализации и переключении ролей
  // TEAM_MEMBER: всегда group.id
  // TEAM_LEAD: group.id только если teamLead === null, иначе null
  useEffect(() => {
    if (!data?.id) {
      setValue('group_id', null);
      return;
    }

    if (role === roles.TEAM_MEMBER) {
      setValue('group_id', data.id);
    } else if (role === roles.TEAM_LEAD) {
      setValue('group_id', !data?.teamLead ? data.id : null);
    }
  }, [data?.id, data?.teamLead, role, setValue]);

  // Загрузка данных при открытии селекта (с кешированием)
  // Для TEAM_LEAD: загружаем группы без тимлида
  // Для TEAM_MEMBER: загружаем все группы в Redux
  const handleSelectOpen = useCallback(async () => {
    if (role === roles.TEAM_LEAD) {
      if (!groupsWithoutTeamLeadLoaded) {
        const groups = await fetchGroupsWithoutTeamLead();
        if (groups) setGroupsWithoutTeamLead(groups);
        setGroupsWithoutTeamLeadLoaded(true);
      }
    } else {
      if (!allGroupsLoaded) {
        await dispatch(fetchAllGroups());
        setAllGroupsLoaded(true);
      }
    }
  }, [
    role,
    groupsWithoutTeamLeadLoaded,
    allGroupsLoaded,
    fetchGroupsWithoutTeamLead,
    dispatch,
  ]);

  return (
    <>
      <DialogContent>
        <div className="form">
          <FormGroup label={t('role')}>
            <TabsComponent
              fullWidth
              isGroup
              value={role}
              setValue={setRole}
              options={memberRoles}
            />
          </FormGroup>

          <MemberNameInput control={control} errors={errors} />
          <EmailInput control={control} errors={errors} />
          <TelegramInput control={control} errors={errors} />

          <InviteTeamPicker
            control={control}
            errors={errors}
            groupsWithoutTeamLead={groupsWithoutTeamLead}
            role={role}
            group={data}
            onSelectOpen={handleSelectOpen}
            loading={
              role === roles.TEAM_LEAD
                ? !groupsWithoutTeamLeadLoaded
                : !allGroupsLoaded
            }
          />
        </div>
      </DialogContent>

      <DialogActions>
        <ThemeButton
          onClick={handleSubmit(onSubmit)}
          endIcon="chevron-right"
          loading={isLoading}
          block={false}
          minWidth
        >
          {t('invite')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
